import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import {
    Trash2, RotateCcw, AlertTriangle, RefreshCw, Package, CalendarDays, Users, ClipboardList,
    UserCheck, FileText, Receipt, ListTodo, CalendarRange, Search,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
    prospect: { label: 'Prospect', icon: Users, color: 'bg-pink-100 text-pink-700 border-pink-200' },
    client: { label: 'Client', icon: UserCheck, color: 'bg-blue-100 text-blue-700 border-blue-200' },
    devis: { label: 'Devis', icon: FileText, color: 'bg-amber-100 text-amber-700 border-amber-200' },
    facture: { label: 'Facture', icon: Receipt, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    event: { label: 'Rendez-vous', icon: CalendarDays, color: 'bg-blue-100 text-blue-700 border-blue-200' },
    agenda_event: { label: 'Agenda déménagement', icon: CalendarRange, color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    inventaire: { label: 'Inventaire', icon: ClipboardList, color: 'bg-violet-100 text-violet-700 border-violet-200' },
    inventaire_prospect: { label: 'Inventaire prospect', icon: ClipboardList, color: 'bg-violet-100 text-violet-700 border-violet-200' },
    task: { label: 'Tâche', icon: ListTodo, color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        const d = parseISO(dateStr);
        if (!isValid(d)) return dateStr;
        return format(d, "d MMM yyyy 'à' HH:mm", { locale: fr });
    } catch { return dateStr; }
}

function itemKey(it) { return `${it.type}|${it.id}`; }

export default function Corbeille() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmAction, setConfirmAction] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [filterType, setFilterType] = useState('all');
    const [search, setSearch] = useState('');

    const isAdmin = user?.role === 'admin';

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/corbeille`, { withCredentials: true });
            setItems(res.data || []);
            setSelected(new Set());
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur chargement corbeille');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter(it => {
            if (filterType !== 'all' && it.type !== filterType) return false;
            if (!q) return true;
            return [it.name, it.detail, it.deleted_by_name, ...(it.remarques || []).map(r => r.remarque)]
                .filter(Boolean).some(s => String(s).toLowerCase().includes(q));
        });
    }, [items, filterType, search]);

    const allFilteredKeys = useMemo(() => filtered.map(itemKey), [filtered]);
    const allSelected = filtered.length > 0 && allFilteredKeys.every(k => selected.has(k));
    const someSelected = !allSelected && allFilteredKeys.some(k => selected.has(k));

    const toggleAll = () => {
        if (allSelected) {
            const next = new Set(selected);
            allFilteredKeys.forEach(k => next.delete(k));
            setSelected(next);
        } else {
            const next = new Set(selected);
            allFilteredKeys.forEach(k => next.add(k));
            setSelected(next);
        }
    };
    const toggleOne = (it) => {
        const k = itemKey(it);
        const next = new Set(selected);
        if (next.has(k)) next.delete(k); else next.add(k);
        setSelected(next);
    };

    const handleRestore = async (item) => {
        setProcessing(true);
        try {
            await axios.post(`${API_URL}/api/corbeille/restore/${item.type}/${item.id}`, {}, { withCredentials: true });
            toast.success(`${item.type_label} « ${item.name} » restauré`);
            setItems(prev => prev.filter(i => !(i.type === item.type && i.id === item.id)));
            setSelected(prev => { const n = new Set(prev); n.delete(itemKey(item)); return n; });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur restauration');
        } finally { setProcessing(false); setConfirmAction(null); }
    };

    const handlePermanentDelete = async (item) => {
        setProcessing(true);
        try {
            await axios.delete(`${API_URL}/api/corbeille/${item.type}/${item.id}`, { withCredentials: true });
            toast.success(`${item.type_label} supprimé définitivement`);
            setItems(prev => prev.filter(i => !(i.type === item.type && i.id === item.id)));
            setSelected(prev => { const n = new Set(prev); n.delete(itemKey(item)); return n; });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur suppression');
        } finally { setProcessing(false); setConfirmAction(null); }
    };

    const handleBulk = async (kind) => {
        if (selected.size === 0) return;
        const payload = { items: items.filter(it => selected.has(itemKey(it))).map(it => ({ type: it.type, id: it.id })) };
        setProcessing(true);
        try {
            const url = kind === 'restore' ? `${API_URL}/api/corbeille/bulk/restore` : `${API_URL}/api/corbeille/bulk/delete`;
            const res = await axios.post(url, payload, { withCredentials: true });
            const okCount = res.data.restored ?? res.data.deleted ?? 0;
            toast.success(`${okCount} élément(s) ${kind === 'restore' ? 'restauré(s)' : 'supprimé(s) définitivement'}`);
            await fetchItems();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur traitement');
        } finally {
            setProcessing(false);
            setConfirmAction(null);
        }
    };

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            <div data-testid="corbeille-page">
                {/* Header */}
                <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Trash2 className="w-6 h-6 text-[#1B365D]" />
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="corbeille-title">
                                Corbeille
                            </h1>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {items.length} élément{items.length !== 1 ? 's' : ''} · Suppression auto après 30 jours
                            </p>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchItems} disabled={loading} className="gap-1.5 h-8 text-xs" data-testid="refresh-corbeille">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Actualiser
                    </Button>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher (nom, remarque, utilisateur…)" className="pl-8 h-9 text-sm" data-testid="corbeille-search" />
                    </div>
                    <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="border border-gray-200 rounded-md h-9 px-2.5 text-sm bg-white"
                        data-testid="corbeille-filter-type"
                    >
                        <option value="all">Tous les types</option>
                        {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                </div>

                {/* Bulk actions */}
                {selected.size > 0 && (
                    <div className="bg-[#1B365D] text-white rounded-xl px-4 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-actions-bar">
                        <span className="text-sm font-semibold">
                            {selected.size} élément{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="h-8 text-xs bg-white text-emerald-700 border-white hover:bg-emerald-50"
                                disabled={processing}
                                onClick={() => setConfirmAction({ action: 'bulk_restore' })}
                                data-testid="bulk-restore-btn"
                            >
                                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Tout restaurer
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs bg-white text-red-700 border-white hover:bg-red-50"
                                disabled={processing}
                                onClick={() => setConfirmAction({ action: 'bulk_delete' })}
                                data-testid="bulk-delete-btn"
                            >
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Tout supprimer
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 text-xs text-white hover:bg-white/10"
                                onClick={() => setSelected(new Set())}
                                data-testid="bulk-clear-btn">
                                Désélectionner
                            </Button>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                            <Package className="h-10 w-10 mb-2 text-gray-300" />
                            <p className="text-sm font-medium">{items.length === 0 ? 'La corbeille est vide' : 'Aucun résultat avec ces filtres'}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-[#1B365D]/[0.03]">
                                        <TableHead className="w-10">
                                            <Checkbox
                                                checked={allSelected || (someSelected && 'indeterminate')}
                                                onCheckedChange={toggleAll}
                                                aria-label="Tout sélectionner"
                                                data-testid="select-all"
                                            />
                                        </TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Élément</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Type</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Détails</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Dernières remarques</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Supprimé le</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Par</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D] text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((item) => {
                                        const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.prospect;
                                        const Icon = cfg.icon;
                                        const k = itemKey(item);
                                        const isChecked = selected.has(k);
                                        return (
                                            <TableRow key={k}
                                                data-testid={`corbeille-item-${item.id}`}
                                                className={`transition-colors ${isChecked ? 'bg-blue-50/40' : 'hover:bg-red-50/30'}`}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={isChecked}
                                                        onCheckedChange={() => toggleOne(item)}
                                                        aria-label={`Sélectionner ${item.name}`}
                                                        data-testid={`select-${item.type}-${item.id}`}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                                                            <Icon className="w-4 h-4 text-gray-500" />
                                                        </div>
                                                        <span className="font-medium text-sm text-gray-900">{item.name}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={`${cfg.color} text-xs border`}>{item.type_label}</Badge>
                                                </TableCell>
                                                <TableCell className="text-sm text-gray-500 max-w-[200px] truncate" title={item.detail}>{item.detail}</TableCell>
                                                <TableCell className="max-w-[300px]">
                                                    {item.remarques && item.remarques.length > 0 ? (
                                                        <div className="space-y-1">
                                                            {item.remarques.map((r, i) => (
                                                                <div key={i} className="text-[11px] text-gray-600 leading-tight" title={r.remarque}>
                                                                    <span className="font-semibold text-gray-700">{r.label} :</span>{' '}
                                                                    <span className="italic">« {r.remarque.length > 70 ? r.remarque.slice(0, 70) + '…' : r.remarque} »</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-300">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-sm text-gray-500 whitespace-nowrap">{formatDate(item.deleted_at)}</TableCell>
                                                <TableCell className="text-sm text-gray-500">{item.deleted_by_name}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1.5 justify-end">
                                                        <Button variant="outline" size="sm"
                                                            onClick={() => setConfirmAction({ action: 'restore', item })}
                                                            className="gap-1 h-7 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                                            disabled={processing}
                                                            data-testid={`restore-${item.id}`}>
                                                            <RotateCcw className="w-3 h-3" /> Restaurer
                                                        </Button>
                                                        {isAdmin && (
                                                            <Button variant="outline" size="sm"
                                                                onClick={() => setConfirmAction({ action: 'delete', item })}
                                                                className="gap-1 h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                                                disabled={processing}
                                                                data-testid={`permanent-delete-${item.id}`}>
                                                                <Trash2 className="w-3 h-3" /> Définitif
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Dialog */}
            <Dialog open={!!confirmAction} onOpenChange={() => !processing && setConfirmAction(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className={`flex items-center gap-2 ${(confirmAction?.action || '').includes('delete') ? 'text-red-600' : 'text-emerald-600'}`}>
                            {(confirmAction?.action || '').includes('delete') ? (
                                <><AlertTriangle className="w-5 h-5" /> Suppression définitive</>
                            ) : (
                                <><RotateCcw className="w-5 h-5" /> Restaurer</>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            {confirmAction?.action === 'delete'
                                ? "Cette action est irréversible. L'élément sera supprimé définitivement de la base de données."
                                : confirmAction?.action === 'restore'
                                    ? `L'élément « ${confirmAction?.item?.name} » sera restauré à son emplacement d'origine.`
                                    : confirmAction?.action === 'bulk_restore'
                                        ? `Restaurer les ${selected.size} élément(s) sélectionné(s) à leur emplacement d'origine ?`
                                        : `Cette action est irréversible. ${selected.size} élément(s) seront supprimés définitivement.`
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={processing}>Annuler</Button>
                        {confirmAction?.action === 'delete' ? (
                            <Button disabled={processing} onClick={() => handlePermanentDelete(confirmAction.item)}
                                className="bg-red-600 hover:bg-red-700 text-white gap-1.5" data-testid="confirm-permanent-delete">
                                <Trash2 className="w-4 h-4" /> {processing ? 'Suppression…' : 'Supprimer définitivement'}
                            </Button>
                        ) : confirmAction?.action === 'restore' ? (
                            <Button disabled={processing} onClick={() => handleRestore(confirmAction.item)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="confirm-restore">
                                <RotateCcw className="w-4 h-4" /> {processing ? 'Restauration…' : 'Restaurer'}
                            </Button>
                        ) : confirmAction?.action === 'bulk_restore' ? (
                            <Button disabled={processing} onClick={() => handleBulk('restore')}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="confirm-bulk-restore">
                                <RotateCcw className="w-4 h-4" /> {processing ? 'Restauration…' : `Restaurer ${selected.size}`}
                            </Button>
                        ) : (
                            <Button disabled={processing} onClick={() => handleBulk('delete')}
                                className="bg-red-600 hover:bg-red-700 text-white gap-1.5" data-testid="confirm-bulk-delete">
                                <Trash2 className="w-4 h-4" /> {processing ? 'Suppression…' : `Supprimer ${selected.size}`}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
